import { useCallback, useEffect, useMemo, useState } from 'react'
import MainLayout from '../layout/MainLayout/MainLayout'
import { IconAddWindow, IconPlus } from '../layout/NavRail/NavRail'
import NavLocation from '../layout/NavLocation/NavLocation'
import CodeBreadcrumb from '../components/code/CodeBreadcrumb'
import NavList from '../layout/NavList/NavList'
import { NavFilterLead, useFilterOpen } from '../layout/NavList/NavFilterLead'
import {
  NotebookList,
  NotebookWindowBody,
  notebookLabel,
  collectSubtreeIds,
} from '../components/notebooks'
import { burrowTrail } from '../components/notebooks/notebookTree'
import { WindowGrid, GridDock, useWindowGrid } from '../components/grid'
import { MultiSelect } from '../components/inputs'
import { useNotebookCache } from '../hooks/useNotebookCache'
import { useTopics } from '../hooks/useTopics'
import { useModal } from '../context/ModalContext'
import { topicLabelFor } from '../utils/topicLabel'
import {
  listNotebooks,
  createNotebook,
  updateNotebook,
  deleteNotebook,
} from '../services/notebooks'
import { usePageTitle } from '../components/DocumentTitle'

const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const IconEdit = () => (
  <svg {...svgProps}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
)

const IconDelete = () => (
  <svg {...svgProps}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
)

const IconArchive = () => (
  <svg {...svgProps}>
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
)

// Notebooks workspace: sidebar tree beside a tiled window grid. Each window
// holds one page (editor | PDF). Sharing is owner-only in this pass.
function NotebooksPage() {
  const { openModal } = useModal()
  const [pages, setPages] = useState(null)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const { topics: topicOptions } = useTopics()
  const [topicFilter, setTopicFilter] = useState([])
  const [filterOpen, toggleFilter] = useFilterOpen('ct-notebooks-filter-open')

  const grid = useWindowGrid({
    storageKey: 'ct-grid-notebooks-v1',
    limits: { maxWindows: 6, maxPerColumn: 2, maxColumns: 3 },
    initialType: 'notebook',
  })

  const entityIds = useMemo(
    () => grid.columns.flatMap((col) => col.windows.map((w) => w.entityId)).filter(Boolean),
    [grid.columns]
  )
  const { pagesById, seed, remove } = useNotebookCache(entityIds)

  const activePageId = useMemo(() => {
    for (const col of grid.columns) {
      const win = col.windows.find((w) => w.id === grid.activeId)
      if (win) return win.entityId
    }
    return null
  }, [grid.columns, grid.activeId])

  const refresh = useCallback(() => {
    return listNotebooks()
      .then((list) => {
        setPages(list)
        setError('')
      })
      .catch(() => setError('Could not load notes.'))
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  useEffect(() => {
    if (!notice) return undefined
    const timer = setTimeout(() => setNotice(''), 4000)
    return () => clearTimeout(timer)
  }, [notice])

  const openPage = useCallback(
    (page) => {
      if (!page?._id) return
      if (Array.isArray(page.blocks)) seed(page)
      grid.selectEntity({ type: 'notebook', entityId: page._id })
    },
    [seed, grid]
  )

  const handleAddWindow = useCallback(() => {
    if (grid.isFull) {
      setNotice(
        `You can open up to ${grid.limits.maxWindows} windows at once. Close one to add another.`
      )
      return
    }
    grid.addWindow({ type: 'notebook' })
  }, [grid])

  const handleNew = async () => {
    const page = await createNotebook({ title: 'Untitled' })
    await refresh()
    openPage(page)
  }

  const [burrowId, setBurrowId] = useState(null)

  const createChild = useCallback(
    async (parent) => {
      const created = await createNotebook({ title: 'Untitled', parent: parent._id })
      setBurrowId(String(parent._id))
      await refresh()
      return created
    },
    [refresh]
  )

  const handleNewChild = async (parent) => {
    const page = await createChild(parent)
    openPage(page)
  }

  const patchList = useCallback((updated) => {
    if (!updated?._id) return
    setPages((prev) => {
      if (!prev) return prev
      return prev.map((p) =>
        p._id === updated._id
          ? {
              ...p,
              title: updated.title,
              icon: updated.icon,
              updatedAt: updated.updatedAt,
              topics: updated.topics,
            }
          : p
      )
    })
  }, [])

  const handleSaved = useCallback(
    (updated) => {
      seed(updated)
      patchList(updated)
    },
    [seed, patchList]
  )

  const clearWindowsForIds = useCallback(
    (ids) => {
      const set = new Set(ids.map(String))
      grid.columns.forEach((col) =>
        col.windows.forEach((w) => {
          if (w.entityId && set.has(String(w.entityId))) grid.setWindowEntity(w.id, null)
        })
      )
    },
    [grid]
  )

  const handleRename = useCallback(
    (page) => {
      if (!page) return
      openModal('rename-notebook', {
        page: pagesById[page._id] || page,
        onSaved: handleSaved,
      })
    },
    [openModal, pagesById, handleSaved]
  )

  const handleArchive = useCallback(
    (page) => {
      if (!page) return
      const label = notebookLabel(page)
      const ids = collectSubtreeIds(pages || [], page._id)
      openModal('confirm', {
        title: 'Archive page',
        message: `Archive "${label}" and its nested pages? They will leave the sidebar.`,
        confirmLabel: 'Archive',
        onConfirm: () => Promise.all(ids.map((id) => updateNotebook(id, { archived: true }))),
        onConfirmed: () => {
          clearWindowsForIds(ids)
          ids.forEach((id) => remove(id))
          refresh()
        },
      })
    },
    [openModal, pages, clearWindowsForIds, remove, refresh]
  )

  const forgetPages = useCallback(
    (ids) => {
      const gone = new Set(ids.map(String))
      setPages((prev) => (prev ? prev.filter((entry) => !gone.has(String(entry._id))) : prev))
      clearWindowsForIds(ids)
      ids.forEach((id) => remove(id))
      setBurrowId((current) => (current && gone.has(String(current)) ? null : current))
    },
    [clearWindowsForIds, remove]
  )

  const handleDelete = useCallback(
    (page) => {
      if (!page) return
      const label = notebookLabel(page)
      openModal('confirm', {
        title: 'Delete page',
        message: `Delete "${label}" and all nested pages? This can't be undone.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => deleteNotebook(page._id),
        onConfirmed: () => {
          forgetPages(collectSubtreeIds(pages || [], page._id))
          refresh()
        },
      })
    },
    [openModal, pages, forgetPages, refresh]
  )

  const handleDeleteBlock = useCallback(
    ({ pageId, title, onDeleted }) => {
      if (!pageId) return
      openModal('confirm', {
        title: 'Delete page',
        message: `Delete "${title || 'this page'}" and its nested pages? This can't be undone.`,
        confirmLabel: 'Delete',
        danger: true,
        onConfirm: () => deleteNotebook(pageId),
        onConfirmed: () => {
          forgetPages(collectSubtreeIds(pages || [], pageId))
          onDeleted?.()
          refresh()
        },
      })
    },
    [openModal, pages, forgetPages, refresh]
  )

  const getTitle = useCallback(
    (win) => {
      if (!win.entityId) return 'New window'
      const full = pagesById[win.entityId]
      if (full) return notebookLabel(full)
      const listed = (pages || []).find((p) => p._id === win.entityId)
      return listed ? notebookLabel(listed) : 'Loading…'
    },
    [pagesById, pages]
  )
  usePageTitle(activePageId ? getTitle({ entityId: activePageId }) : '')

  const renderWindow = useCallback(
    (win) => {
      if (!win.entityId) {
        return <NotebookWindowBody page={null} />
      }
      const page = pagesById[win.entityId]
      return (
        <NotebookWindowBody
          page={page || null}
          loading={!page}
          panes={win.panes}
          onPanesChange={(layout) => grid.setPanes(win.id, layout)}
          onSaved={handleSaved}
          onCreatePage={page ? () => createChild(page) : undefined}
          onOpenPage={openPage}
          onDeletePage={handleDeleteBlock}
          pageTitle={(id) => {
            const found = (pages || []).find((entry) => String(entry._id) === String(id))
            return found ? notebookLabel(found) : ''
          }}
          pagePath={page ? burrowTrail(pages || [], page._id) : []}
        />
      )
    },
    [pagesById, grid, handleSaved, createChild, openPage, handleDeleteBlock, pages]
  )

  const renderMenuItems = useCallback(
    (win) => {
      const page = win.entityId
        ? pagesById[win.entityId] || (pages || []).find((p) => p._id === win.entityId)
        : null
      if (!page) return null
      return (
        <>
          <button type="button" role="menuitem" onClick={() => handleRename(page)}>
            <IconEdit />
            Rename
          </button>
          <button type="button" role="menuitem" onClick={() => handleArchive(page)}>
            <IconArchive />
            Archive
          </button>
          <button
            type="button"
            role="menuitem"
            className="is-danger"
            onClick={() => handleDelete(page)}
          >
            <IconDelete />
            Delete
          </button>
        </>
      )
    },
    [pagesById, pages, handleRename, handleArchive, handleDelete]
  )

  const trail = useMemo(() => burrowTrail(pages, burrowId), [pages, burrowId])
  useEffect(() => {
    if (burrowId && !trail.length) setBurrowId(null)
  }, [burrowId, trail.length])

  const filterActive = topicFilter.length > 0
  const visiblePages = useMemo(() => {
    if (!pages || !filterActive) return pages
    const want = new Set(topicFilter.map((t) => String(t._id)))
    return pages.filter((p) =>
      (p.topics || []).some((t) => want.has(String(t?._id ?? t)))
    )
  }, [pages, filterActive, topicFilter])

  return (
    <MainLayout
      storageId="notebooks-layout"
      navLocation={
        <NavLocation>
          <div className="code-locationbar">
            <CodeBreadcrumb
              rootLabel="Notes"
              trail={trail.map((entry) => ({ label: notebookLabel(entry) }))}
              onNavigate={(index) =>
                setBurrowId(index < 0 || !trail[index] ? null : String(trail[index]._id))
              }
            />
          </div>
        </NavLocation>
      }
      navActions={[
        {
          key: 'add-window',
          label: 'Add window',
          icon: <IconAddWindow />,
          onClick: handleAddWindow,
        },
        {
          key: 'new',
          label: 'New note',
          icon: <IconPlus />,
          onClick: handleNew,
        },
      ]}
      navDock={
        <GridDock
          items={grid.minimized}
          getTitle={getTitle}
          onRestore={grid.restoreWindow}
          onClose={grid.closeWindow}
        />
      }
      nav={
        <NavList>
          {topicOptions.length ? (
            <>
              <NavFilterLead
                open={filterOpen}
                active={filterActive}
                onToggle={toggleFilter}
                onClear={() => setTopicFilter([])}
                clearDisabled={!filterActive}
                last={!filterOpen}
              />
              {filterOpen ? (
                <div className="nav-topic-filter">
                  <span className="nav-filter-label">Topics</span>
                  <MultiSelect
                    value={topicFilter}
                    onChange={setTopicFilter}
                    options={topicOptions}
                    getOptionValue={(t) => t._id}
                    getOptionLabel={topicLabelFor(topicOptions)}
                    placeholder="Any topic"
                    clearQueryOnSelect={false}
                  />
                </div>
              ) : null}
            </>
          ) : null}
          <NotebookList
            pages={visiblePages}
            flat={filterActive}
            selectedId={activePageId}
            onSelect={openPage}
            onNewChild={handleNewChild}
            onRename={handleRename}
            onArchive={handleArchive}
            onDelete={handleDelete}
            burrowId={filterActive ? null : burrowId}
            onBurrow={(page) => setBurrowId(String(page._id))}
            error={error}
          />
        </NavList>
      }
      display={
        <div className="sr-grid-wrap">
          {notice && <div className="sr-grid-notice">{notice}</div>}
          <div className="sr-grid-body">
            <WindowGrid
              grid={grid}
              renderWindow={renderWindow}
              getTitle={getTitle}
              renderMenuItems={renderMenuItems}
              empty="Select or create a note to get started."
            />
          </div>
        </div>
      }
    />
  )
}

export default NotebooksPage
